import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { AdminSidebarLayout } from './SidebarLayout';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Award, RefreshCw, Trash2, Search, Loader2, AlertCircle,
  CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Pagination } from './ListControls';

const formatDate = (s) => (s ? new Date(s).toLocaleString('en-PH', { dateStyle: 'short', timeStyle: 'short' }) : '—');

export function AdminStockCertificatesPage() {
  const { api } = useAuth();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ total_certs: 0, active_certs: 0, total_units_issued: 0, total_units_sold: 0 });
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [limit, setLimit] = useState(20);
  const [search, setSearch] = useState('');
  const [tier, setTier] = useState('all');
  const [chunks, setChunks] = useState('all');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [availablePlans, setAvailablePlans] = useState([]);

  // Load active subscription plans once for the Tier filter dropdown.
  // Keeps Admin → Stock Certificates in sync with Admin → Plans.
  useEffect(() => {
    api.get('/api/admin/subscription-plans?limit=100')
      .then(({ data }) => setAvailablePlans((data?.plans || []).filter((p) => p.is_active !== false)))
      .catch(() => {});
  }, [api]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ skip, limit });
      if (search) params.append('search', search);
      if (tier && tier !== 'all') params.append('tier', tier);
      if (chunks && chunks !== 'all') params.append('chunks', chunks);
      const { data } = await api.get(`/api/admin/stock-certificates?${params}`);
      setRows(data.certificates || []);
      setTotal(data.total || 0);
      setSummary(data.summary || {});
    } catch (e) {
      toast.error('Failed to load stock certificates');
    }
    setLoading(false);
  }, [api, skip, limit, search, tier, chunks]);

  useEffect(() => { fetchRows(); }, [fetchRows]);
  useEffect(() => { setSkip(0); }, [search, tier, chunks, limit]);

  const recompute = async (certId) => {
    setBusyId(certId);
    try {
      const { data } = await api.post(`/api/admin/stock-certificates/${certId}/recompute`);
      toast.success(`Recomputed — ${data.result?.earned_units || 0} units`);
      fetchRows();
    } catch (e) { toast.error(e.response?.data?.detail || 'Recompute failed'); }
    setBusyId(null);
  };

  const revoke = async (certId) => {
    if (!window.confirm('Revoke this certificate? base_units will be set to 0. Trade history is preserved.')) return;
    setBusyId(certId);
    try {
      await api.delete(`/api/admin/stock-certificates/${certId}`);
      toast.success('Certificate revoked');
      fetchRows();
    } catch (e) { toast.error(e.response?.data?.detail || 'Revoke failed'); }
    setBusyId(null);
  };

  return (
    <AdminSidebarLayout>
      <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto" data-testid="admin-stock-certificates-page">
        <div className="mb-8">
          <h1 className="font-outfit text-2xl font-bold text-white flex items-center gap-2">
            <Award className="w-6 h-6 text-[#FFEA00]" /> Stock Certificates
          </h1>
          <p className="text-[#A0B4CC] text-sm mt-1">
            Monitor every issued certificate. Issuance rule: $500 USD per chunk, capped by each member&apos;s active plan tier.
          </p>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <SummaryTile label="Total certificates" value={summary.total_certs} color="#00E5FF" testid="summary-total" />
          <SummaryTile label="Active (issued)" value={summary.active_certs} color="#00E676" testid="summary-active" />
          <SummaryTile label="Units issued" value={`$${Number(summary.total_units_issued || 0).toLocaleString()}`} color="#FFEA00" testid="summary-units-issued" />
          <SummaryTile label="Units sold (P2P)" value={`$${Number(summary.total_units_sold || 0).toLocaleString()}`} color="#A0B4CC" testid="summary-units-sold" />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C7599]" />
            <Input
              placeholder="Search by name or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-[#041A3B] border-white/10 text-white"
              data-testid="cert-search"
            />
          </div>
          <select value={tier} onChange={(e) => setTier(e.target.value)}
            className="h-10 px-3 rounded-md bg-[#041A3B] border border-white/10 text-white text-sm"
            data-testid="cert-tier-filter">
            <option value="all">All tiers</option>
            {availablePlans.map((p) => (
              <option key={p.id} value={p.name}>{p.name}</option>
            ))}
          </select>
          <select value={chunks} onChange={(e) => setChunks(e.target.value)}
            className="h-10 px-3 rounded-md bg-[#041A3B] border border-white/10 text-white text-sm"
            data-testid="cert-status-filter">
            <option value="all">All statuses</option>
            <option value="issued">Issued (≥ 1 chunk)</option>
            <option value="pending">Pending (0 chunks)</option>
            <option value="maxed">Maxed out</option>
          </select>
          <Button onClick={fetchRows} variant="ghost" className="text-[#00E5FF]" data-testid="cert-refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>

        {/* Rows */}
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-[#00E5FF]" /></div>
        ) : rows.length === 0 ? (
          <div className="text-center py-16 text-[#5C7599]">
            <Award className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No certificates match your filters</p>
          </div>
        ) : (
          <div className="space-y-2">
            {rows.map((c) => {
              const filled = c.chunks_issued || 0;
              const max = c.max_chunks || 0;
              return (
                <Card key={c.id} className="bg-[#041A3B] border-white/10" data-testid={`cert-row-${c.id}`}>
                  <CardContent className="p-4 flex flex-wrap items-center gap-4">
                    <div className="flex-1 min-w-[200px]">
                      <p className="text-white text-sm font-medium">{c.owner_name || 'Unknown'}</p>
                      <p className="text-[#A0B4CC] text-xs">{c.owner_email || '—'}</p>
                      <p className="text-[#5C7599] text-[10px] mt-0.5">
                        Plan: <span className="text-[#A0B4CC]">{c.owner_plan || '—'}</span>
                        {' · '}Updated {formatDate(c.updated_at || c.granted_at)}
                      </p>
                    </div>

                    {/* Chunks pills */}
                    <div className="flex items-center gap-1.5" title={`${filled} of ${max} chunks issued`}>
                      {Array.from({ length: Math.max(1, max) }).map((_, i) => (
                        <span
                          key={i}
                          className={`w-6 h-6 rounded-md text-[10px] flex items-center justify-center font-medium border ${
                            i < filled
                              ? 'bg-[#00E676]/20 text-[#00E676] border-[#00E676]/30'
                              : 'bg-white/5 text-[#5C7599] border-white/10'
                          }`}
                        >${(c.chunk_size_usd || 500) / 1000}k</span>
                      ))}
                      <span className="ml-2 text-xs font-mono text-white tabular-nums">
                        ${Number(c.base_units || 0).toLocaleString()}
                      </span>
                      {c.is_maxed && <CheckCircle2 className="w-4 h-4 text-[#00E676]" title="Maxed out" />}
                    </div>

                    <div className="flex items-center gap-1.5">
                      <Button
                        size="sm" variant="ghost"
                        disabled={busyId === c.id}
                        onClick={() => recompute(c.id)}
                        className="text-[#00E5FF] hover:bg-[#00E5FF]/10"
                        data-testid={`cert-recompute-${c.id}`}
                        title="Re-run evaluation"
                      >
                        {busyId === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                      </Button>
                      {c.base_units > 0 && (
                        <Button
                          size="sm" variant="ghost"
                          disabled={busyId === c.id}
                          onClick={() => revoke(c.id)}
                          className="text-[#FF1744] hover:bg-[#FF1744]/10"
                          data-testid={`cert-revoke-${c.id}`}
                          title="Revoke (set base_units to 0)"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        <Pagination
          skip={skip}
          limit={limit}
          total={total}
          onPageChange={setSkip}
          onLimitChange={setLimit}
        />

        <div className="mt-6 flex items-start gap-2 text-[11px] text-[#5C7599]">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <p>
            Tip: certificates are auto-recomputed on every member visit to <code>/stocks</code>.
            Click the <RefreshCw className="inline w-3 h-3" /> icon to force a recompute now, or
            <Trash2 className="inline w-3 h-3 text-[#FF1744] mx-1" /> to manually revoke an erroneous issuance.
          </p>
        </div>
      </div>
    </AdminSidebarLayout>
  );
}

function SummaryTile({ label, value, color, testid }) {
  return (
    <Card className="bg-[#041A3B] border-white/10" data-testid={testid}>
      <CardContent className="p-4">
        <p className="text-[#5C7599] text-[10px] uppercase tracking-wider">{label}</p>
        <p className="font-outfit text-xl text-white font-semibold tabular-nums mt-1" style={{ color }}>
          {value}
        </p>
      </CardContent>
    </Card>
  );
}

export default AdminStockCertificatesPage;
